import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, History } from 'lucide-react';
import { hasAtLeastRole } from '@portal/shared';
import { AppShell, Badge, Card, CardBody, CardHeader, Spinner } from '../components/index.js';
import { useAuth } from '../lib/auth-context.js';
import { TOOLS, TOOL_ICONS } from '../lib/tools.js';

export function Dashboard() {
  const { me, loading } = useAuth();

  if (loading) {
    return (
      <AppShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner size="lg" />
        </div>
      </AppShell>
    );
  }

  // me=null with loading=false is a network/transient failure (403 ACCESS_DENIED
  // is handled by RequireAuth via accessDenied flag and never lands here).
  // Show a recoverable error rather than redirecting — a redirect loop with
  // Home is otherwise possible while the session is still valid.
  if (!me) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto px-8 py-16">
          <Card>
            <CardBody>
              <div className="flex items-start gap-3">
                <AlertCircle className="size-5 text-danger shrink-0 mt-1" aria-hidden />
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">Couldn’t load your profile.</h2>
                  <p className="mt-1 text-sm text-text-secondary">
                    The server didn’t return your portal user record. Refresh to retry, or sign out and back in if it persists.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-8 py-10 space-y-8">
        <header>
          <h1 className="text-4xl font-bold tracking-tight text-text-primary">TAS Portal</h1>
          <p className="mt-3 flex flex-wrap items-center gap-3 text-base text-text-secondary">
            <span>Signed in as {me.name || me.email}</span>
            <Badge variant={roleVariant(me.role)}>{me.role}</Badge>
          </p>
        </header>

        <section aria-labelledby="tools-heading" className="space-y-4">
          <div className="flex items-baseline justify-between">
            <h2 id="tools-heading" className="text-xl font-semibold text-text-primary">
              Tools
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {TOOLS.filter((t) => !t.hiddenFromDashboard).map((tool) => {
              const Icon = TOOL_ICONS[tool.iconKey];
              return (
                <Link
                  key={tool.id}
                  to={`/tools/${tool.pathSegment}`}
                  className="group block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-accent-base"
                >
                  <Card className="h-full transition-colors group-hover:border-border-strong">
                    <CardHeader
                      title={
                        <div className="flex items-center gap-3">
                          <span className="inline-flex size-10 items-center justify-center rounded-lg bg-accent-muted text-accent-hover">
                            <Icon className="size-5" aria-hidden />
                          </span>
                          <span>{tool.name}</span>
                        </div>
                      }
                    />
                    <CardBody>
                      <p className="text-sm text-text-secondary">{tool.description}</p>
                      <p className="mt-4 flex items-center gap-1 text-sm font-medium text-accent-base group-hover:text-accent-hover">
                        Open
                        <ArrowRight className="size-4" aria-hidden />
                      </p>
                    </CardBody>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {hasAtLeastRole(me.role, 'admin') && (
          <section aria-labelledby="admin-heading" className="space-y-4">
            <h2 id="admin-heading" className="text-xl font-semibold text-text-primary">
              Admin
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Link
                to="/internal/corrections"
                className="group block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-accent-base"
              >
                <Card className="h-full transition-colors group-hover:border-border-strong">
                  <CardHeader
                    title={
                      <div className="flex items-center gap-3">
                        <span className="inline-flex size-10 items-center justify-center rounded-lg bg-accent-muted text-accent-hover">
                          <History className="size-5" aria-hidden />
                        </span>
                        <span>Activity log</span>
                      </div>
                    }
                  />
                  <CardBody>
                    <p className="text-sm text-text-secondary">
                      Dictation edits and price overrides across the portal — find patterns the STT and pricing engines should learn.
                    </p>
                    <p className="mt-4 flex items-center gap-1 text-sm font-medium text-accent-base group-hover:text-accent-hover">
                      Open
                      <ArrowRight className="size-4" aria-hidden />
                    </p>
                  </CardBody>
                </Card>
              </Link>
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}

function roleVariant(role: string): 'accent' | 'success' | 'warning' | 'neutral' {
  switch (role) {
    case 'owner':
      return 'success';
    case 'admin':
      return 'accent';
    case 'staff':
      return 'neutral';
    case 'viewer':
    default:
      return 'warning';
  }
}
